import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import SEOMeta from '@/components/base/SEOMeta';
import JsonLd from '@/components/base/JsonLd';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您访问的文章不存在或已被删除。</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${article.title} - 蜜桃网站`}
        description={article.excerpt}
        keywords={`蜜桃网站,${article.tags.join(',')},${article.title}`}
        canonicalUrl={`https://peachspot.github.io/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrl}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          image: article.imageUrl,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          datePublished: article.date,
          publisher: {
            '@type': 'Organization',
            name: '蜜桃网站',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://peachspot.github.io/article/${article.id}`,
          },
          about: {
            '@type': 'Thing',
            name: article.tags[0],
          },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://peachspot.github.io',
            },
            ...(category
              ? [
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: category.name,
                    item: `https://peachspot.github.io/category/${category.slug}`,
                  },
                ]
              : []),
            {
              '@type': 'ListItem',
              position: category ? 3 : 2,
              name: article.title,
              item: `https://peachspot.github.io/article/${article.id}`,
            },
          ],
        }}
      />

      <Navbar />

      <main className="pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          {/* Breadcrumbs */}
          <div className="mb-6">
            <Breadcrumbs
              items={[
                { label: '首页', href: '/' },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />
          </div>

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="text-xs px-3 py-1 rounded-full bg-accent-100 text-accent-800 font-medium whitespace-nowrap hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  {category.name}
                </Link>
              )}
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 font-medium whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-600 flex-wrap">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 次阅读
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
              title={article.title}
            />
          </div>

          {/* Article Content */}
          <div
            className="article-content prose prose-base max-w-none mb-12"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Share */}
          <div className="flex items-center gap-3 py-6 border-t border-background-200">
            <span className="text-sm text-foreground-600 whitespace-nowrap">分享文章：</span>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
              <i className="ri-weibo-line"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
              <i className="ri-wechat-line"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
              <i className="ri-link"></i>
            </button>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.id} {...related} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}